import {
  ArrowLeft, Check, ChevronDown, CircleDot, ClipboardCheck, Lock, SkipForward, Split,
} from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { AnswerInput } from "@/features/intake-live/answer-input";
import {
  answerText, mayReopen, maySkip, mustAnswer, type Walk, type WalkStep,
} from "@/lib/intake-steps";
import type { IntakeStep } from "@/lib/queries-intake";
import { cn } from "@/lib/utils";

/**
 * The intake as one question at a time (ACL-403).
 *
 * **One list, not a question and a history.** Every step the walk has reached is a row: the one
 * being asked is open with its input, the ones behind are collapsed to their answer and open on a
 * click. That shape is the whole screen — an earlier draft had the current question in a card and
 * the answered ones in a separate fold below, and the two read as unrelated panels. A rep looking
 * for what they said two questions ago should find it where they left it, in the line above.
 *
 * **Forward is the server's decision, not this component's.** Answering posts the one value and
 * the response is the whole new state: the route, the next question, the outcome. That is what
 * keeps the conditional, state, firm and campaign rules in one evaluator instead of two, and it
 * is ACL-403's own requirement — "the next question is determined from the configured flow rather
 * than hard-coded logic".
 *
 * So there is no Next button. Submitting an answer *is* Next, because the answer is what decides
 * where next is. Opening an earlier row and changing it re-walks from there; the abandoned branch
 * simply stops being on the route, with nothing to clean up (ADR-110).
 *
 * **No progress bar.** A conditional flow has no fixed length — two leads walking the same flow
 * see a different number of questions — so a percentage would be wrong as often as right. The
 * header counts what is answered and what the engine can still see ahead.
 *
 * `renderDecision` fills in what the flow settled for itself: the firm or campaign a split went
 * by, and the picker when that is the missing fact. It is drawn at the point the walk passed it,
 * because to a rep "it went to everyone else" is a step of the intake like any other — and
 * because a split explains the questions that follow it, which it cannot do from the bottom.
 */
export function IntakeWizard({
  walk,
  pending,
  problem,
  onAnswer,
  onSkip,
  renderDecision,
}: {
  walk: Walk;
  pending: boolean;
  problem: string | null;
  onAnswer: (step: IntakeStep, value: unknown) => void;
  onSkip: (step: IntakeStep) => void;
  /** What the flow settled for itself at this step — the firm or campaign it went by, and the
   *  picker when that is the missing fact. Drawn where the walk passed it. */
  renderDecision?: (key: string) => React.ReactNode;
}) {
  // Which answered row the rep has opened. Cleared whenever the engine moves on, so a saved
  // change hands the screen back to whatever is now being asked.
  const [open, setOpen] = React.useState<string | null>(null);
  const currentKey = walk.current?.step.key ?? null;
  React.useEffect(() => setOpen(null), [currentKey, walk.settled]);

  if (walk.steps.length === 0) {
    return (
      <EmptyState
        icon={ClipboardCheck}
        title="Nothing to ask"
        description="This flow has no question waiting for this contact."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <Header walk={walk} />
      <ol className="divide-y divide-border">
        {walk.steps.map((one) =>
          one.decision ? (
            // Where the walk actually passed it, not after every question: a firm split explains
            // the questions that FOLLOW it, so appending it below them put the explanation after
            // the thing it explains.
            <li key={one.step.key} className="border-l-2 border-transparent bg-surface-sunken/60 px-3 py-2.5">
              <DecidedRow title={one.step.title}>{renderDecision?.(one.step.key)}</DecidedRow>
            </li>
          ) : (
            <li key={one.step.key}>
              <Row
                one={one}
                opened={open === one.step.key}
                pending={pending}
                problem={problem}
                onOpen={() => setOpen(open === one.step.key ? null : one.step.key)}
                onAnswer={onAnswer}
                onSkip={onSkip}
              />
            </li>
          ),
        )}
      </ol>
      {walk.settled ? <Settled count={walk.done} /> : null}
    </div>
  );
}

/** What is answered and what is still ahead. Counts, never a percentage. */
function Header({ walk }: { walk: Walk }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-surface-sunken px-3 py-2">
      {/* Where the rep is, not what the pane is: the pane's own header already says "Intake" and
          whether it is complete, so repeating the verdict here made two title bands saying the
          same thing. This one carries the only fact the header above cannot — which question of
          how many. */}
      <span className="text-xs font-medium text-text">
        {walk.settled ? "Every question answered" : `Question ${walk.current?.position ?? walk.done + 1}`}
      </span>
      <Badge tone={walk.settled ? "success" : "neutral"} size="sm" className="tabular-nums">
        {walk.done} answered
        {/* `steps_left` is what the engine can see ahead, which a branch may change — so "more",
            never a total, or it would read as a promise. */}
        {!walk.settled && walk.left > 0 ? ` · ${walk.left} more` : ""}
      </Badge>
    </div>
  );
}

function Row({
  one, opened, pending, problem, onOpen, onAnswer, onSkip,
}: {
  one: WalkStep;
  opened: boolean;
  pending: boolean;
  problem: string | null;
  onOpen: () => void;
  onAnswer: (step: IntakeStep, value: unknown) => void;
  onSkip: (step: IntakeStep) => void;
}) {
  const step = one.step;
  const showing = one.current || opened;
  const canOpen = one.done && mayReopen(step);

  return (
    <div
      className={cn(
        "relative border-l-2 border-transparent",
        one.current && "border-accent-9 bg-accent-2",
        // The row is one hit area when it can be opened, and the focus ring is drawn here, around
        // the whole row — not around the question and its answer, which is what a button wrapping
        // only the text produced: a rectangle floating inside the row with the tick and the
        // chevron left outside it. The button gives up its own outline for this, so the ring is
        // restated here at the app's own weight (`index.css`'s 2px) rather than left as a tint a
        // keyboard user could miss.
        canOpen &&
          "cursor-pointer hover:bg-surface-hover focus-within:outline focus-within:outline-2 " +
            "focus-within:-outline-offset-2 focus-within:outline-focus",
      )}
    >
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <StepMark one={one} />
        <div className="min-w-0 flex-1">
          {/* The whole row opens it, so a rep aiming at what they wrote does not miss. The button
              holds only the title: an answer's input must never be inside the control that
              collapses the row, and the ring belongs to the row (above), not to a rectangle drawn
              around the question and its answer with the tick and chevron left outside it. */}
          {canOpen ? (
            <button
              type="button"
              /* `before:` stretches the hit area over the whole row without nesting the answer
                 inside the control: the ring then belongs to the row, via `focus-within`. */
              className="group flex w-full items-start gap-2 text-left outline-none before:absolute before:inset-0 before:content-['']"
              aria-expanded={opened}
              onClick={onOpen}
            >
              <StepTitle step={step} answered={!opened} />
              <ChevronDown
                className={cn(
                  "mt-0.5 size-3.5 shrink-0 text-text-subtle transition-transform group-hover:text-text-muted",
                  opened && "rotate-180",
                )}
                aria-hidden
              />
            </button>
          ) : (
            <StepTitle step={step} answered={one.done} asking={one.current} />
          )}

          {showing ? (
            <div className="mt-2">
              {!step.readable ? (
                <Locked>
                  This question is not yours to see. Somebody with access has to answer it before
                  the intake can go on.
                </Locked>
              ) : !step.may_answer ? (
                <Locked>You can read this question but not answer it. A supervisor records this one.</Locked>
              ) : (
                <AnswerInput
                  step={step}
                  pending={pending}
                  problem={problem}
                  onSubmit={(value) => onAnswer(step, value)}
                  onCancel={opened && !one.current ? onOpen : undefined}
                />
              )}
              {opened && !one.current ? (
                <p className="mt-1.5 text-2xs text-text-subtle">
                  Changing this re-reads the rest of the intake: answers that no longer apply stop
                  counting, and the route follows the new one.
                </p>
              ) : null}
              {/* Only where the flow allows it: the server refuses a required question with a
                  409, so the button would be a control that always fails. */}
              {one.current && maySkip(step) ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-1.5 h-auto gap-1 px-1.5 py-1 text-xs"
                  disabled={pending}
                  onClick={() => onSkip(step)}
                >
                  <SkipForward className="size-3.5" aria-hidden />
                  Skip this question
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The question and, once it is behind the rep, what was said to it. Shared with the
 *  read-only readback (ACL-477), which draws the same rows without the inputs.
 *
 *  "Required" is drawn only on the question being asked (`asking`), where it says why there is no
 *  Skip. Most questions are must-answer, so on every row it would be noise (ACL-539). */
export function StepTitle({
  step, answered, asking = false,
}: { step: IntakeStep; answered: boolean; asking?: boolean }) {
  const said = answerText(step);
  return (
    <span className="min-w-0 flex-1">
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="text-sm font-medium leading-snug text-text">{step.title}</span>
        {asking && mustAnswer(step) ? (
          <span className="text-2xs font-medium uppercase tracking-wide text-text-subtle">Required</span>
        ) : null}
      </span>
      {answered ? (
        <span className="mt-0.5 block truncate text-xs text-text-muted">
          {step.status === "skipped" ? <em>Skipped</em> : said}
        </span>
      ) : null}
    </span>
  );
}

/** Where this step stands, as one glyph. Shared with the readback (ACL-477). */
export function StepMark({ one }: { one: WalkStep }) {
  if (one.current) {
    return (
      <span
        className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-accent-9 text-2xs font-semibold text-white tabular-nums dark:text-gray-1"
        aria-hidden
      >
        {one.position}
      </span>
    );
  }
  if (one.step.status === "skipped") {
    return (
      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border border-border text-text-subtle" aria-hidden>
        <SkipForward className="size-3" />
      </span>
    );
  }
  if (one.step.status === "flagged") {
    return (
      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-warning-4 text-warning-11" aria-hidden>
        <CircleDot className="size-3" />
      </span>
    );
  }
  if (one.done) {
    return (
      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-success-4 text-success-11" aria-hidden>
        <Check className="size-3" />
      </span>
    );
  }
  return (
    <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border border-border text-2xs text-text-subtle tabular-nums" aria-hidden>
      {one.position}
    </span>
  );
}

/** A question this reader may not see or may not answer. Shared with the readback. */
export function Locked({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 rounded-md bg-surface-sunken px-2.5 py-2 text-xs text-text-muted">
      <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

/** The foot of a finished run. Every answer above stays open to correction. */
function Settled({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-1.5 border-t border-success-4 bg-success-2 px-3 py-2 text-xs text-success-11">
      <Check className="size-3.5" aria-hidden />
      {count} answered. Any of them can still be changed, and the intake re-reads from there.
    </div>
  );
}

/**
 * A step the flow decided for itself, as a row of the list (ACL-339, ACL-340).
 *
 * Exported for the panel, which is where the firm picker and the reason live: this is only the
 * shape they sit in, so the list reads the same whether a row is a question or a decision.
 */
function DecidedRow({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border border-border text-text-subtle" aria-hidden>
        <Split className="size-3" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug text-text">
          {title || "Varies by firm or campaign"}
        </p>
        {children}
      </div>
    </div>
  );
}
